import jsPDF from 'jspdf';

// ── Logo loader (same as generateReciboPDF) ─────────────────────────────────
async function loadLogoDataUrl() {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = function () {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(null); return; }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = '/lux_logo.PNG';
    } catch { resolve(null); }
  });
}

/**
 * Genera y descarga el PDF del Kardex de un estudiante.
 * Diseño basado en el formato oficial de Universidad Lux.
 *
 * FUENTE ÚNICA DE VERDAD: los datos vienen ya consolidados desde el backend
 * (`materias` + `resumen`). El PDF NO recalcula promedios, NO deriva notas de
 * parciales y NO reagrupa: solo dibuja lo que el servicio entrega, de modo que
 * la Vista Previa y el PDF sean idénticos.
 *
 * @param {object} opts
 * @param {object}  opts.encabezado   - { nombre_completo, matricula, programa, campus, curp, institucion }
 * @param {Array}   opts.materias     - [{ materia_nombre, creditos, estatus, final, revalidacion,
 *                                         oportunidades: [{ orden, tipo, nombre_ciclo, calificacion }] }]
 * @param {object}  opts.resumen      - { materias_aprobadas, total_materias_plan, creditos_obtenidos, promedio_general }
 * @param {boolean} [opts.descargar=true]
 */
export async function generateKardexPDF({ encabezado, materias, resumen = {}, descargar = true }) {
  const doc   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();   // 210
  const pageH = doc.internal.pageSize.getHeight();  // 297
  const mL = 10;   // left margin
  const mR = 10;   // right margin
  const mB = 10;   // bottom margin reserved for footer
  const contentW = pageW - mL - mR; // 190

  const logoDataUrl = await loadLogoDataUrl();

  // ── Column layout ──────────────────────────────────────────────────────────
  // Asignatura:72 | Cr.:7 | 1a.:13 C:11 | 2a.:13 C:11 | 3a.:13 C:11 | 4a.:13 C:11 | Rev.:15  = 190
  const cols = [
    { key: 'asig', x: mL,      w: 72, label: 'Asignatura', align: 'left'   },
    { key: 'cr',   x: mL+72,   w:  7, label: 'Cr.',        align: 'center' },
    { key: 'g1',   x: mL+79,   w: 13, label: '1a.',        align: 'center' },
    { key: 'c1',   x: mL+92,   w: 11, label: 'Calif.',     align: 'center' },
    { key: 'g2',   x: mL+103,  w: 13, label: '2a.',        align: 'center' },
    { key: 'c2',   x: mL+116,  w: 11, label: 'Calif.',     align: 'center' },
    { key: 'g3',   x: mL+127,  w: 13, label: '3a.',        align: 'center' },
    { key: 'c3',   x: mL+140,  w: 11, label: 'Calif.',     align: 'center' },
    { key: 'g4',   x: mL+151,  w: 13, label: '4a.',        align: 'center' },
    { key: 'c4',   x: mL+164,  w: 11, label: 'Calif.',     align: 'center' },
    { key: 'rev',  x: mL+175,  w: 15, label: 'Rev.',       align: 'center' },
  ];

  // ── Data preparation ───────────────────────────────────────────────────────
  // Los datos ya vienen consolidados desde el backend: un solo renglón por
  // materia, oportunidades ordenadas por tipo (1ª ordinario, 2ª extraordinario,
  // 3ª+ recursamiento) y calificaciones que son ÚNICAMENTE la final oficial
  // (null cuando el ciclo está en curso). El PDF no reagrupa ni recalcula.
  const rows = (materias || []).filter((m) => (m.creditos ?? 0) > 0).map((m) => ({
    nombre: m.materia_nombre || '',
    creditos: m.creditos,
    revalidacion: m.revalidacion,
    attempts: (m.oportunidades || []).map((o) => ({
      ciclo: o.nombre_ciclo || '',
      calificacion: o.calificacion,
    })),
  }));

  // ── Drawing helpers ────────────────────────────────────────────────────────
  const fmtGrade = (val) => {
    if (val === null || val === undefined) return '';
    const n = parseFloat(val);
    return isNaN(n) ? '' : (Number.isInteger(n) ? String(n) : n.toFixed(1));
  };

  const cell = (text, col, cy, bold = false, red = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setTextColor(...(red ? [185, 28, 28] : [0, 0, 0]));
    const tx = col.align === 'center' ? col.x + col.w / 2 : col.x + 1;
    doc.text(String(text ?? ''), tx, cy, { align: col.align === 'center' ? 'center' : 'left' });
    doc.setTextColor(0, 0, 0);
  };

  // ── Page header (drawn on every page) ─────────────────────────────────────
  let y = 0;

  const drawPageHeader = () => {
    y = 10;

    // Logo – top right
    const logoW = 22;
    const logoH = 18;
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', pageW - mR - logoW, y - 2, logoW, logoH);
    }

    // Institution name – centered
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(encabezado.institucion || 'Universidad Lux', pageW / 2, y + 5, { align: 'center' });

    // Address – centered
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(50, 50, 50);
    doc.text('Ave. Venustiano Carranza Nte. Monterrey', pageW / 2, y + 10, { align: 'center' });

    y += 15;

    // Separator line
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.line(mL, y, mL + contentW, y);
    y += 4;

    // Student info row
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(0, 0, 0);
    doc.text(`Nombre : ${encabezado.nombre_completo || ''}`, mL, y);
    doc.text(`Matricula : ${encabezado.matricula || ''}`, mL + 110, y);
    y += 5;
    doc.text(`Carrera : ${encabezado.programa || encabezado.codigo_programa || ''}`, mL, y);
    if (encabezado.modalidad) {
      doc.text(`Modalidad : ${encabezado.modalidad}`, mL + 110, y);
    }
    doc.text(`RVOE:AL-VI 079/2013`, pageW - mR, y, { align: 'right' });
    y += 5;

    // Column headers – bold
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    cols.forEach((col) => cell(col.label, col, y, true));
    y += 1;

    // Underline below column headers
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.4);
    doc.line(mL, y, mL + contentW, y);
    y += 4;
  };

  // ── First page ─────────────────────────────────────────────────────────────
  drawPageHeader();

  // ── Table rows ─────────────────────────────────────────────────────────────
  const rowH  = 4.0;
  const rowFS = 7.5;

  for (const row of rows) {
    if (y + rowH > pageH - mB) {
      doc.addPage();
      drawPageHeader();
    }

    doc.setFontSize(rowFS);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    // Asignatura (truncate if needed)
    const asigText = row.nombre.length > 42 ? row.nombre.slice(0, 41) + '…' : row.nombre;
    cell(asigText, cols[0], y);

    // Créditos
    cell(row.creditos ?? '', cols[1], y);

    // Attempts up to 4 → columns g1/c1 … g4/c4
    const attemptPairs = [
      { g: cols[2], c: cols[3] },
      { g: cols[4], c: cols[5] },
      { g: cols[6], c: cols[7] },
      { g: cols[8], c: cols[9] },
    ];

    row.attempts.slice(0, 4).forEach((attempt, i) => {
      cell(attempt.ciclo, attemptPairs[i].g, y);
      const gStr   = fmtGrade(attempt.calificacion);
      const failing = gStr !== '' && parseFloat(gStr) < 6;
      cell(gStr, attemptPairs[i].c, y, false, failing);
    });

    // Revalidación / equivalencia (columna Rev.)
    cell(fmtGrade(row.revalidacion), cols[10], y);

    y += rowH;
  }

  // ── Summary block (last page, after table rows) ───────────────────────────
  // Totales tomados DIRECTAMENTE del resumen del backend (misma fuente que la
  // Vista Previa). El PDF no recalcula: garantiza correspondencia absoluta.
  const totalMaterias     = resumen.total_materias_plan ?? rows.length;
  const materiasAprobadas  = resumen.materias_aprobadas ?? 0;
  const totalCreditos     = resumen.creditos_obtenidos ?? 0;
  const promedioGeneral   = (resumen.promedio_general !== null && resumen.promedio_general !== undefined)
    ? resumen.promedio_general
    : '—';

  // Add new page if not enough space
  const summaryHeight = 18;
  if (y + summaryHeight > pageH - mB - 6) {
    doc.addPage();
    y = 18;
  } else {
    y += 3;
  }

  // Separator line above summary
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(mL, y, mL + contentW, y);
  y += 4;

  // Left block – text lines
  const now2 = new Date();
  const fechaExpide = now2.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text(
    `Este documento ampara ${materiasAprobadas} Materias aprobadas de ${totalMaterias} que corresponden al plan de estudios.`,
    mL, y
  );
  y += 4;
  doc.text(`Se expide en la Ciudad de Monterrey, N. L. el dia ${fechaExpide}`, mL, y);
  y += 4;
  doc.text('Dirección de Control Escolar y Archivo', mL, y);

  // Right block – totals
  const rightX = pageW - mR;
  const summaryTopY = y - 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text('Total de Cred.Cubiertos', rightX - 40, summaryTopY, { align: 'right' });
  doc.text(String(totalCreditos), rightX, summaryTopY, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.text('PROMEDIO GENERAL', rightX - 40, summaryTopY + 6, { align: 'right' });
  doc.text(String(promedioGeneral), rightX, summaryTopY + 6, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);

  y += 8;
  const totalPages = doc.internal.getNumberOfPages();
  const now        = new Date();
  const fechaGen   = now.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.3);
    doc.line(mL, pageH - mB + 2, mL + contentW, pageH - mB + 2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(`Documento generado el ${fechaGen}`, mL, pageH - mB + 6);
    doc.text(`Página ${i} de ${totalPages}`, pageW - mR, pageH - mB + 6, { align: 'right' });
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  const safeName = (encabezado.matricula || 'estudiante').replace(/[^a-zA-Z0-9_-]/g, '_');
  if (descargar) {
    doc.save(`Kardex_${safeName}.pdf`);
  } else {
    return doc.output('dataurlstring');
  }
}
